import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SenseHint } from './SenseChooser.js';
import {
  App as AntApp,
  Breadcrumb,
  Button,
  Dropdown,
  Flex,
  Input,
  Modal,
  Result,
  Skeleton,
  Table,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, EllipsisOutlined } from '@ant-design/icons';
import { DeckWordStatus, TranslationLanguage, stripArticle } from '@lingoping/core';
import { TranslationText } from './TranslationText.js';
import {
  CloudDecksApi,
  CloudDeckWord,
  DeckLists,
  NewDeckWord,
  OpenDeck,
  getCloudDecksApi,
} from '../services/cloudDecks.js';
import {
  DeckDecision,
  applyDeckDecision,
  deckLearnQueue,
  relearnKnownWord,
} from '../services/deckDecisions.js';
import {
  EMPTY_LEARNER,
  LearnerState,
  classifyDeck,
  deckProgress,
  loadLearnerState,
} from '../services/deckStatus.js';
import { getBaseTranslationLanguage } from '../services/translationSettings.js';
import {
  deckWordArticle,
  deckWordLemma,
  deckWordMeaning,
  deckWordPalette,
} from '../utils/deckWords.js';

const { Text, Title } = Typography;

/** One word per line; "word<TAB>translation" keeps the translation as a hint. */
export function parseWordLines(text: string): NewDeckWord[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [term, ...rest] = line.split('\t');
      return { lemma: stripArticle(term.trim()), hint: rest.join(' ').trim() || null };
    })
    .filter((w) => w.lemma.length > 0);
}

const STATUS_LABEL: Record<
  Exclude<DeckWordStatus, 'undecided'>,
  { text: string; style: React.CSSProperties }
> = {
  known: { text: 'Known', style: { background: '#e6f4ff', color: '#0958d9' } },
  learning: { text: 'Learning', style: { background: '#f0fdf4', color: '#15803d' } },
  waiting: { text: 'Waiting to be learned', style: { background: '#fff7e6', color: '#ad4e00' } },
};

const PAGE_SIZE = 50;

interface Props {
  deckId: string;
  userId: string;
  /** False while another tab is showing. */
  isActive?: boolean;
  onBack: () => void;
  onGoThrough: (deckId: string) => void;
  /** Something changed that other tabs show (a card, a known word). */
  onChanged: () => void;
}

interface Row {
  word: CloudDeckWord;
  index: number;
  status: DeckWordStatus;
  deferred: boolean;
  next: boolean;
}

export const DeckPage: React.FC<Props> = ({
  deckId,
  userId,
  isActive = true,
  onBack,
  onGoThrough,
  onChanged,
}) => {
  const { message, modal } = AntApp.useApp();
  const [api, setApi] = useState<CloudDecksApi | null>(null);
  const [open, setOpen] = useState<OpenDeck | null>(null);
  const [lists, setLists] = useState<DeckLists | null>(null);
  const [learner, setLearner] = useState<LearnerState>(EMPTY_LEARNER);
  const [language, setLanguage] = useState<TranslationLanguage>('en');
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState<Set<string>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [addText, setAddText] = useState('');
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameText, setRenameText] = useState('');
  const pagePicked = useRef(false);

  const reloadLearner = useCallback(async () => setLearner(await loadLearnerState()), []);

  const reloadLocal = useCallback(
    async (service: CloudDecksApi) => {
      const [deck, nextLists] = await Promise.all([service.readDeck(deckId), service.readLists()]);
      setOpen(deck);
      setLists(nextLists);
    },
    [deckId]
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const service = await getCloudDecksApi();
      if (cancelled) return;
      setApi(service);
      await reloadLocal(service);
      void reloadLearner();
      void getBaseTranslationLanguage().then(setLanguage);
      // The saved copy is on screen; now bring it up to date.
      const { online } = await service.refreshLists();
      await service.refreshDeck(deckId, { force: true });
      if (cancelled) return;
      await reloadLocal(service);
      setOffline(!online);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [deckId, reloadLocal, reloadLearner]);

  // Cards are made in the background after "Learn": keep the statuses honest.
  useEffect(() => {
    const sync = () => {
      setAdding(new Set(deckLearnQueue.active().map((j) => j.word.id)));
      void reloadLearner();
    };
    return deckLearnQueue.subscribe(sync);
  }, [reloadLearner]);

  // Coming back to this tab: cards may have changed in the review window.
  useEffect(() => {
    if (isActive) void reloadLearner();
  }, [isActive, reloadLearner]);

  const deck = open?.deck ?? null;
  const personal = !!deck?.owner_id;
  const following = !!deck && lists?.followingId === deck.id;

  const classification = useMemo(
    () => (open ? classifyDeck(open.words, learner, lists?.deferrals ?? {}) : null),
    [open, learner, lists]
  );
  const progress = classification ? deckProgress(classification) : null;
  const nextId = following ? classification?.undecided[0]?.id : undefined;

  const rows: Row[] = useMemo(
    () =>
      (classification?.words ?? []).map((c, index) => ({
        word: c.word as CloudDeckWord,
        index: index + 1,
        status: c.status,
        deferred: !!c.deferredAt,
        next: c.word.id === nextId,
      })),
    [classification, nextId]
  );

  // A followed deck opens on the page with the word Echo asks about next.
  useEffect(() => {
    if (pagePicked.current || rows.length === 0 || !following) return;
    pagePicked.current = true;
    const at = rows.findIndex((r) => r.next);
    if (at >= 0) setPage(Math.floor(at / PAGE_SIZE) + 1);
  }, [rows, following]);

  const decide = async (word: CloudDeckWord, decision: DeckDecision) => {
    try {
      await applyDeckDecision(word, decision, { userId, deckId, api: api ?? undefined });
      await reloadLearner();
      onChanged();
    } catch (err) {
      console.error('Could not save the choice:', err);
      message.error("That choice couldn't be saved. Please try again.");
    }
  };

  const relearn = async (word: CloudDeckWord) => {
    try {
      await relearnKnownWord(word, { userId, deckId, api: api ?? undefined });
      await reloadLearner();
      onChanged();
      message.success(`“${deckWordLemma(word)}” is back in your learning.`);
    } catch (err) {
      console.error('Could not move the word back to learning:', err);
      message.error("That couldn't be saved. Please try again.");
    }
  };

  const toggleFollow = async () => {
    if (!api || !deck) return;
    await api.follow(following ? null : deck.id);
    await reloadLocal(api);
    message.success(
      following ? `Stopped following “${deck.title}”.` : `Following “${deck.title}”.`
    );
  };

  const addWords = async () => {
    if (!api || !deck) return;
    const words = parseWordLines(addText);
    if (words.length === 0) return;
    const { added, skipped } = await api.addWords(deck.id, words);
    void api.flush().then(async (uploaded) => {
      for (const text of await api.takeFailures()) message.warning(text);
      // The server links words it already has in the library: read the rows again for that.
      if (uploaded) {
        await api.refreshDeck(deck.id, { force: true });
        await reloadLocal(api);
      }
    });
    setAddOpen(false);
    setAddText('');
    await reloadLocal(api);
    message.success(
      `Added ${added} word${added === 1 ? '' : 's'}${skipped > 0 ? `, ${skipped} already in the deck or not usable` : ''}.`
    );
  };

  const rename = async () => {
    if (!api || !deck || !renameText.trim()) return;
    await api.renameDeck(deck.id, renameText);
    setRenameOpen(false);
    await reloadLocal(api);
  };

  const confirmDelete = () => {
    if (!api || !deck) return;
    modal.confirm({
      title: `Delete “${deck.title}”?`,
      content: 'Your cards and progress stay.',
      okText: 'Delete deck',
      okButtonProps: { danger: true },
      onOk: async () => {
        await api.deleteDeck(deck.id);
        onChanged();
        onBack();
      },
    });
  };

  const removeWord = async (word: CloudDeckWord) => {
    if (!api || !deck) return;
    await api.removeWord(deck.id, word.id);
    await reloadLocal(api);
  };

  if (!deck && loading) return <Skeleton active />;
  if (!deck) {
    return (
      <Result
        status="404"
        title="This deck isn't available"
        subTitle="It may have been deleted, or it hasn't been loaded yet."
        extra={
          <Button type="primary" onClick={onBack}>
            Back to decks
          </Button>
        }
      />
    );
  }

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 64,
      render: (v: number) => <Text type="secondary">{v}</Text>,
    },
    {
      title: 'Word',
      key: 'word',
      width: 240,
      render: (_: unknown, row: Row) => {
        const article = deckWordArticle(row.word);
        return (
          <span>
            {article && (
              <span style={{ color: deckWordPalette(row.word).color, fontWeight: 600 }}>
                {article}{' '}
              </span>
            )}
            <strong>{deckWordLemma(row.word)}</strong>
            <SenseHint hint={row.word.sense_hint ?? row.word.word?.sense_hint} />
          </span>
        );
      },
    },
    {
      title: 'Meaning',
      key: 'meaning',
      render: (_: unknown, row: Row) => {
        const meaning = deckWordMeaning(row.word, language);
        if (meaning) {
          return <TranslationText as="span" text={meaning.text} lang={meaning.textLang} />;
        }
        if (row.word.sense_label) return <Text>{row.word.sense_label}</Text>;
        return (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {row.word.hint
              ? `${row.word.hint} (from your file)`
              : 'Meaning appears when the card is prepared'}
          </Text>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 200,
      render: (_: unknown, row: Row) => {
        if (adding.has(row.word.id)) return <Text type="secondary">Adding to your list…</Text>;
        if (row.status !== 'undecided') {
          const label = STATUS_LABEL[row.status];
          return (
            <span
              style={{
                padding: '2px 10px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                ...label.style,
              }}
            >
              {label.text}
            </span>
          );
        }
        if (row.next) {
          return (
            <Text strong style={{ fontSize: 13, color: '#0958d9' }}>
              Next word Echo asks about
            </Text>
          );
        }
        return (
          <Text type="secondary" style={{ fontSize: 13 }}>
            Not decided yet{row.deferred ? ' (not now)' : ''}
          </Text>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 230,
      align: 'right' as const,
      render: (_: unknown, row: Row) => (
        <Flex gap={6} justify="flex-end">
          {row.status === 'undecided' && !adding.has(row.word.id) && (
            <>
              <Button size="small" onClick={() => void decide(row.word, 'known')}>
                I know it
              </Button>
              <Button size="small" type="primary" onClick={() => void decide(row.word, 'learn')}>
                Learn
              </Button>
            </>
          )}
          {row.status === 'known' && !adding.has(row.word.id) && (
            <Button
              size="small"
              title="Take this word off your known words and learn it"
              onClick={() => void relearn(row.word)}
            >
              Learn it
            </Button>
          )}
          {personal && (
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Remove ${row.word.lemma} from this deck`}
              title="Remove from this deck"
              onClick={() => void removeWord(row.word)}
            />
          )}
        </Flex>
      ),
    },
  ];

  const total = progress?.total ?? deck.word_count;
  const summary = progress
    ? `${total} word${total === 1 ? '' : 's'}, in the order ${personal ? 'you added them' : 'of the deck'} · ${progress.learning} learning · ${progress.known} known · ${progress.toGo} to go`
    : `${total} words`;

  return (
    <Flex vertical gap={16}>
      <Breadcrumb
        items={[
          {
            title: (
              <a
                href="#decks"
                onClick={(e) => {
                  e.preventDefault();
                  onBack();
                }}
              >
                Decks
              </a>
            ),
          },
          { title: deck.title },
        ]}
      />
      {offline && (
        <Text type="secondary" style={{ fontSize: 13 }}>
          You're offline. Changes are saved and sent when you're back online.
        </Text>
      )}
      <div
        style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: '20px 24px',
        }}
      >
        <Flex align="center" gap={16} wrap>
          <Flex vertical gap={6} style={{ flexGrow: 1, minWidth: 260 }}>
            <Flex align="center" gap={10} wrap>
              <Title level={3} style={{ margin: 0 }}>
                {deck.title}
              </Title>
              {following && (
                <Tag color="blue" style={{ margin: 0 }}>
                  Following
                </Tag>
              )}
              {personal && <Tag style={{ margin: 0 }}>Your deck</Tag>}
            </Flex>
            {deck.description && <Text type="secondary">{deck.description}</Text>}
            <Text type="secondary">{summary}</Text>
          </Flex>
          <Flex gap={8} wrap>
            <Button
              type="primary"
              disabled={!progress || progress.toGo === 0}
              onClick={() => onGoThrough(deck.id)}
            >
              Go through words
            </Button>
            <Button onClick={() => void toggleFollow()}>
              {following ? 'Stop following' : 'Follow'}
            </Button>
            {personal && <Button onClick={() => setAddOpen(true)}>Add words</Button>}
            {personal && (
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    { key: 'rename', label: 'Rename deck' },
                    { key: 'delete', label: 'Delete deck', danger: true },
                  ],
                  onClick: ({ key }) => {
                    if (key === 'rename') {
                      setRenameText(deck.title);
                      setRenameOpen(true);
                    } else confirmDelete();
                  },
                }}
              >
                <Button icon={<EllipsisOutlined />} aria-label="More deck actions" />
              </Dropdown>
            )}
          </Flex>
        </Flex>
      </div>

      <Table<Row>
        rowKey={(row) => row.word.id}
        columns={columns}
        dataSource={rows}
        loading={loading && rows.length === 0}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          showSizeChanger: false,
          hideOnSinglePage: true,
          onChange: setPage,
        }}
        onRow={(row) => ({ style: row.next ? { background: '#f8fbff' } : undefined })}
        locale={{
          emptyText: personal
            ? 'No words yet. Use “Add words” to put some in this deck.'
            : 'This deck has no words.',
        }}
      />

      <Modal
        title="Add words"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={() => void addWords()}
        okText="Add to deck"
        okButtonProps={{ disabled: parseWordLines(addText).length === 0 }}
        destroyOnHidden
      >
        <Flex vertical gap={8}>
          <Text type="secondary">One word or phrase per line. Duplicates are skipped.</Text>
          <Input.TextArea
            rows={8}
            aria-label="Words to add"
            value={addText}
            onChange={(e) => setAddText(e.target.value)}
            placeholder={'Fernweh\ngemütlich\ndie Zeitung'}
          />
        </Flex>
      </Modal>

      <Modal
        title="Rename deck"
        open={renameOpen}
        onCancel={() => setRenameOpen(false)}
        onOk={() => void rename()}
        okText="Rename"
        okButtonProps={{ disabled: !renameText.trim() }}
        destroyOnHidden
      >
        <Input
          aria-label="Deck name"
          value={renameText}
          maxLength={120}
          onChange={(e) => setRenameText(e.target.value)}
          onPressEnter={() => void rename()}
          autoFocus
        />
      </Modal>
    </Flex>
  );
};
